import type {ReactElement} from 'react';
import {useState} from 'react';
import {Chip} from '@Components/chip/chip';
import {Dropdown} from '@Components/dropdown-v2';
import {isSmallScreenDropdown} from '@Components/dropdown-v2/dropdown.helper';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {FieldSpacer} from '@Components/social-media-wizard/components/field-spacer';
import type {InstagramAudioTrack} from '@Components/social-media-wizard/social-media-wizard.types';
import {getReelMusicLabel} from '@Libraries/social-media-params.library';
import {useAppDispatch} from '@/hooks';
import {ReelMusicSearch} from '../reel-music-search/reel-music-search';
import {ReelMusicSearchLayout} from '../reel-music-search/reel-music-search.types';
import {useReelMusicField} from './reel-music-field.hooks';
import type {ReelMusicFieldProps} from './reel-music-field.types';
import {
  REEL_MUSIC_MODAL_MAX_HEIGHT,
  REEL_MUSIC_MODAL_PANEL_ID,
  REEL_MUSIC_POPUP_MAX_HEIGHT_PX,
  REEL_MUSIC_POPUP_OFFSET_PX,
  REEL_MUSIC_PICKER_SPACING_CLASSES,
} from './reel-music-field.types';
import styles from './reel-music-field.module.scss';

export function ReelMusicField({account, isVideoDesign}: ReelMusicFieldProps): ReactElement | null {
  const dispatch = useAppDispatch();
  const reelMusicField = useReelMusicField(account, isVideoDesign);
  const [isOpen, setIsOpen] = useState(false);

  if (!reelMusicField.isAvailable) {
    return null;
  }

  const closeSearch = (): void => {
    setIsOpen(false);
    dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
  };

  const selectTrack = (track: InstagramAudioTrack): void => {
    reelMusicField.selectTrack(track);
    closeSearch();
  };

  const getPill = (): ReactElement => {
    if (reelMusicField.reelMusic) {
      return (
        <Chip
          label={getReelMusicLabel(reelMusicField.reelMusic)}
          icon="icon-music"
          isSelected
          isRemovable
          onRemove={reelMusicField.clearReelMusic}
          className={styles.chosenSongPill}
        />
      );
    }

    return <Chip label={window.i18next.t('pmwjs_reel_music_add')} icon="icon-music" isSelected={isOpen} />;
  };

  const getSearch = (layout: ReelMusicSearchLayout): ReactElement => {
    return <ReelMusicSearch accountId={account.id} chosenReelMusic={reelMusicField.reelMusic} onSelectTrack={selectTrack} layout={layout} />;
  };

  const openPicker = (): void => {
    if (isPickerShownAsDropdown()) {
      setIsOpen(true);
    }
  };

  return (
    <>
      <FieldSpacer />
      <div className="flex-h-row">
        <Dropdown
          selector={getPill()}
          popup={getSearch(ReelMusicSearchLayout.DROPDOWN)}
          modalContent={getSearch(ReelMusicSearchLayout.BOTTOM_SHEET)}
          open={isOpen}
          onOpen={openPicker}
          onClose={(): void => {
            setIsOpen(false);
          }}
          doCloseOnInnerClick={false}
          alignment={ALIGNMENT_TYPE.BOTTOM_START}
          position={DROPDOWN_POSITION.FIXED}
          usePortal
          closeOnOuterScroll
          popUpHasCustomWidth
          popupClassName={styles.popup}
          popupSpacingClass={REEL_MUSIC_PICKER_SPACING_CLASSES}
          popupOffset={REEL_MUSIC_POPUP_OFFSET_PX}
          maxHeight={REEL_MUSIC_POPUP_MAX_HEIGHT_PX}
          showModalForSmallScreen
          isSmallScreenModalBottomSheet
          modalTitle={window.i18next.t('pmwjs_reel_music_add')}
          modalClassName={REEL_MUSIC_PICKER_SPACING_CLASSES}
          modalMaxHeight={REEL_MUSIC_MODAL_MAX_HEIGHT}
          panelIdForModal={REEL_MUSIC_MODAL_PANEL_ID}
        />
      </div>
    </>
  );
}

/**
 * Small screens show the bottom sheet, which owns its own open state, so the floating dropdown stays closed there.
 */
function isPickerShownAsDropdown(): boolean {
  return !isSmallScreenDropdown();
}
